import { Flame, Target, Trophy, Zap } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface StreakBadgeProps {
  currentStreak: number;
  longestStreak: number;
  todayCompleted: number;
  dailyGoal: number;
  goalProgress: number;
  totalCompleted: number;
  compact?: boolean;
}

const StreakBadge = ({
  currentStreak,
  longestStreak,
  todayCompleted,
  dailyGoal,
  goalProgress,
  totalCompleted,
  compact = false,
}: StreakBadgeProps) => {
  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-full cursor-default">
              <Flame className={`h-4 w-4 ${currentStreak > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
              <span className="text-sm font-semibold text-foreground">{currentStreak}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="p-4 max-w-xs">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Current Streak</span>
                <span className="font-bold text-orange-500">{currentStreak} days</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Best Streak</span>
                <span className="font-bold">{longestStreak} days</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Completed</span>
                <span className="font-bold">{totalCompleted}</span>
              </div>
              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-muted-foreground">Today's Goal</span>
                  <span className="text-sm font-medium">{todayCompleted}/{dailyGoal}</span>
                </div>
                <Progress value={goalProgress} className="h-2" />
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-foreground">Your Progress</h3>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <Flame className={`h-5 w-5 ${currentStreak > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
          </div>
          <div className="text-2xl font-bold text-foreground">{currentStreak}</div>
          <div className="text-xs text-muted-foreground">Day Streak</div>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <Trophy className="h-5 w-5 text-yellow-500" />
          </div>
          <div className="text-2xl font-bold text-foreground">{longestStreak}</div>
          <div className="text-xs text-muted-foreground">Best Streak</div>
        </div>
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <Target className="h-5 w-5 text-primary" />
          </div>
          <div className="text-2xl font-bold text-foreground">{totalCompleted}</div>
          <div className="text-xs text-muted-foreground">Total Done</div>
        </div>
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Today's Goal</span>
          <span className="text-sm text-muted-foreground">{todayCompleted}/{dailyGoal} checklists</span>
        </div>
        <Progress value={goalProgress} className="h-2" />
        {goalProgress >= 100 && (
          <p className="text-xs text-success mt-2 flex items-center gap-1">
            <span>🎯</span> Daily goal reached!
          </p>
        )}
      </div>
    </div>
  );
};

export default StreakBadge;
